import type { JSX } from 'react';
import { useStore } from 'zustand';
import { applyToPoint } from '../../core/geometry';
import type { Id, Vec2 } from '../../core/model';
import { useProject, useRuntime, useUi } from '../context';
import { HANDLES, type Handle } from './dragMath';
import type { EditorController } from './editorController';
import { hasArea, type PlacedItem } from './sceneLayout';
import { canvasToScreen, type Viewport } from './viewport';

const HANDLE_SIZE = 9;
const ROTATE_DISTANCE = 26;

const CURSORS = ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'];

/** Resize cursor matching the on-screen direction of a handle once the item is rotated. */
export function cursorForHandle(index: number, rotationDegrees: number): string {
  const steps = Math.round(rotationDegrees / 45);
  return CURSORS[(((index + steps) % 8) + 8) % 8]!;
}

function isCropped(placed: PlacedItem): boolean {
  const { crop } = placed.item.transform;
  return crop.left > 0 || crop.right > 0 || crop.top > 0 || crop.bottom > 0;
}

function SelectionBox(props: { placed: PlacedItem; viewport: Viewport; withHandles: boolean }): JSX.Element {
  const { placed, viewport, withHandles } = props;
  const toScreen = (unit: Vec2): Vec2 => canvasToScreen(viewport, applyToPoint(placed.worldBox, unit));
  const corners = placed.corners.map((c) => canvasToScreen(viewport, c));
  const points = corners.map((c) => `${c.x},${c.y}`).join(' ');
  const className = ['editor-box', placed.locked ? 'locked' : '', isCropped(placed) ? 'cropped' : ''].join(' ');

  let rotate: JSX.Element | null = null;
  if (withHandles) {
    const top = toScreen({ x: 0.5, y: 0 });
    const centre = toScreen({ x: 0.5, y: 0.5 });
    const length = Math.hypot(top.x - centre.x, top.y - centre.y) || 1;
    const knob = {
      x: top.x + ((top.x - centre.x) / length) * ROTATE_DISTANCE,
      y: top.y + ((top.y - centre.y) / length) * ROTATE_DISTANCE,
    };
    rotate = (
      <>
        <line className="editor-rotate-line" x1={top.x} y1={top.y} x2={knob.x} y2={knob.y} />
        <circle className="editor-rotate" data-handle="rotate" cx={knob.x} cy={knob.y} r={5.5} />
      </>
    );
  }

  return (
    <g data-item={placed.item.id}>
      <polygon className={className} points={points} />
      {rotate}
      {withHandles
        ? HANDLES.map((handle: Handle, index) => {
            const p = toScreen(handle);
            return (
              <rect
                key={index}
                className="editor-handle"
                data-handle={`${handle.x},${handle.y}`}
                x={p.x - HANDLE_SIZE / 2}
                y={p.y - HANDLE_SIZE / 2}
                width={HANDLE_SIZE}
                height={HANDLE_SIZE}
                style={{ cursor: cursorForHandle(index, placed.item.transform.rotation) }}
              />
            );
          })
        : null}
    </g>
  );
}

export interface OverlayProps {
  sceneId: Id;
  viewport: Viewport;
  controller: EditorController;
}

/**
 * Selection boxes, handles, snap guides and the marquee. This is the only
 * component that re-renders while an item is being dragged.
 */
export function Overlay({ sceneId, viewport, controller }: OverlayProps): JSX.Element {
  // Subscribing to the scene and to source sizes is what keeps the boxes on the pixels.
  useProject((p) => p.scenes[sceneId]);
  useProject((p) => p.video);
  useRuntime((r) => r.sources);
  const selection = useUi((s) => s.selection);
  const transient = useStore(controller.transient);

  const selected = new Set(selection);
  const boxes = controller.placed().filter((p) => selected.has(p.item.id) && hasArea(p));
  const single = boxes.length === 1 && !boxes[0]!.locked;

  const guideExtent = 100_000;
  return (
    <svg className="editor-overlay" aria-hidden="true">
      {boxes.map((placed) => (
        <SelectionBox key={placed.item.id} placed={placed} viewport={viewport} withHandles={single} />
      ))}
      {transient.guides.map((guide, index) => {
        const at = guide.axis === 'x' ? guide.at * viewport.scale + viewport.offsetX : guide.at * viewport.scale + viewport.offsetY;
        return guide.axis === 'x' ? (
          <line key={index} className="editor-guide" x1={at} x2={at} y1={-guideExtent} y2={guideExtent} />
        ) : (
          <line key={index} className="editor-guide" y1={at} y2={at} x1={-guideExtent} x2={guideExtent} />
        );
      })}
      {transient.marquee ? (
        <rect
          className="editor-marquee"
          x={transient.marquee.minX * viewport.scale + viewport.offsetX}
          y={transient.marquee.minY * viewport.scale + viewport.offsetY}
          width={(transient.marquee.maxX - transient.marquee.minX) * viewport.scale}
          height={(transient.marquee.maxY - transient.marquee.minY) * viewport.scale}
        />
      ) : null}
    </svg>
  );
}
